import React, { useCallback, useEffect, useState } from "react";
import { adminApi, ADMIN_API_BASE, getAdminToken } from "@/lib/adminApi";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminLeads() {
    const [q, setQ] = useState("");
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: 100 };
            if (q) params.q = q;
            const { data } = await adminApi.get("/admin/waitlist", { params });
            setRows(data.entries || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error("Couldn't load waitlist");
        } finally { setLoading(false); }
    }, [q, page]);
    useEffect(() => { load(); }, [load]);

    const del = async (email) => {
        if (!window.confirm(`Delete ${email} from waitlist?`)) return;
        try {
            await adminApi.delete(`/admin/waitlist/${encodeURIComponent(email)}`);
            toast.success("Removed");
            load();
        } catch (e) { toast.error("Failed"); }
    };

    const exportCsv = () => {
        const token = getAdminToken();
        fetch(`${ADMIN_API_BASE}/admin/waitlist/export.csv`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => r.blob())
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url; a.download = `flatch-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
                a.click();
            });
    };

    return (
        <div data-testid="admin-leads-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-black">Leads · Waitlist</h1>
                    <p className="text-sm text-white/50">{total.toLocaleString()} signups</p>
                </div>
                <button
                    onClick={exportCsv}
                    data-testid="admin-leads-export-btn"
                    className="h-9 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 text-xs font-semibold flex items-center gap-2"
                >
                    <Download className="h-3.5 w-3.5" /> Export CSV
                </button>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-3">
                <div className="relative">
                    <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                        placeholder="Search email, name or city…"
                        value={q}
                        onChange={(e) => { setQ(e.target.value); setPage(1); }}
                        data-testid="admin-leads-search"
                        className="w-full h-9 pl-9 pr-3 rounded-lg bg-white/5 border border-white/10 text-sm outline-none focus:border-[#E85D5D]"
                    />
                </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left text-[10px] font-black uppercase tracking-widest text-white/50 border-b border-white/10">
                            <th className="px-4 py-3">#</th>
                            <th className="px-4 py-3">Email</th>
                            <th className="px-4 py-3">Name</th>
                            <th className="px-4 py-3">City</th>
                            <th className="px-4 py-3">Joined</th>
                            <th className="px-4 py-3"></th>
                        </tr>
                    </thead>
                    <tbody data-testid="admin-leads-table">
                        {loading ? (
                            <tr><td colSpan={6} className="text-center text-white/50 py-8">Loading…</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan={6} className="text-center text-white/50 py-8">No leads</td></tr>
                        ) : rows.map((r) => (
                            <tr key={r.email} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-lead-row-${r.email}`}>
                                <td className="px-4 py-3 text-white/50">{r.position || "—"}</td>
                                <td className="px-4 py-3">{r.email}</td>
                                <td className="px-4 py-3 text-white/70">{r.name || "—"}</td>
                                <td className="px-4 py-3 text-white/70">{r.city || "—"}</td>
                                <td className="px-4 py-3 text-white/50 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                                <td className="px-4 py-3 text-right">
                                    <button onClick={() => del(r.email)} data-testid={`admin-lead-delete-${r.email}`}
                                        className="text-white/50 hover:text-[#EF4444]">
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
